/**
 * Settings access for the whole SPA (§5.1 #20-#22).
 *
 * Settings are read through one shared query key, so the Feed's media toggle, the app timezone
 * used to format bare epoch-ms fields and the Settings modal itself all observe the same value and
 * repaint together after a save.
 *
 * ASSUMPTION A-5 lives here: `timezone` starts as `null` and the frontend writes the browser zone
 * on the first authenticated load, once per session.
 */

import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { SettingsResponse, SettingsUpdateBody } from '@lw/shared';
import { getSettings, updateSettings } from '../../api/settings';
import { QK } from '../../api/queryKeys';
import { browserTimeZone } from '../../lib/datetime';

export function useSettingsQuery(): UseQueryResult<SettingsResponse, Error> {
  return useQuery({
    queryKey: QK.settings,
    queryFn: ({ signal }) => getSettings(signal),
    staleTime: 60_000,
  });
}

/** The app timezone (PRD "Timezone"); falls back to the browser's while the setting is unset. */
export function useAppTimezone(): string {
  const { data } = useSettingsQuery();
  return data?.timezone ?? browserTimeZone();
}

/** Global media toggle (§9.3) — defaults to off until settings have loaded. */
export function useMediaEnabled(): boolean {
  const { data } = useSettingsQuery();
  return data?.mediaEnabled ?? false;
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SettingsUpdateBody) => updateSettings(body),
    onSuccess: (settings) => {
      queryClient.setQueryData(QK.settings, settings);
      // Counted post types / timezone / scrape hour all move server-computed figures and
      // schedules (§5.1 #21) — re-read every view and the homescreen rather than guess.
      void queryClient.invalidateQueries({ queryKey: QK.views });
      void queryClient.invalidateQueries({ queryKey: QK.profiles });
      void queryClient.invalidateQueries({ queryKey: QK.status });
    },
  });
}

/** One attempt per page load, shared by every screen that mounts the hook. */
let timezoneBootstrapAttempted = false;

/**
 * ASSUMPTION A-5: `settings.timezone` is `null` until the frontend writes the browser zone on the
 * first authenticated load. Mounted by the screens, not by the shell, so the app/* contracts stay
 * untouched.
 */
export function useTimezoneBootstrap(): void {
  const { data } = useSettingsQuery();
  const queryClient = useQueryClient();
  const running = useRef(false);

  useEffect(() => {
    if (data === undefined || data.timezone !== null) return;
    if (timezoneBootstrapAttempted || running.current) return;
    timezoneBootstrapAttempted = true;
    running.current = true;
    void updateSettings({ timezone: browserTimeZone() })
      .then((settings) => {
        queryClient.setQueryData(QK.settings, settings);
      })
      .catch(() => {
        // A failed write is not worth interrupting the screen for: the server falls back to UTC
        // (§4.1) and the Settings modal still offers the zone picker.
      })
      .finally(() => {
        running.current = false;
      });
  }, [data, queryClient]);
}
